import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import SpinnerOverlay from '../../components/ui/SpinnerOverlay';
import SuccessToast from '../../components/ui/SuccessToast';

const BookingRequestsScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const vehicleId = new URLSearchParams(location?.search)?.get('vehicleId');

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [activeTab, setActiveTab] = useState('requested');

  useEffect(() => {
    if (user) fetchBookings();
  }, [user]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      let query = supabase?.from('rental_bookings')
        ?.select(`*, vehicles(title, make, model), user_profiles!rental_bookings_customer_id_fkey(full_name, phone, email)`)
        ?.eq('owner_id', user?.id)
        ?.order('created_at', { ascending: false });
      if (vehicleId) query = query?.eq('vehicle_id', vehicleId);
      const { data, error } = await query;
      if (error) {
        if (error?.message?.includes('AbortError') || error?.message?.includes('Lock broken')) {
          setTimeout(() => fetchBookings(), 1000);
          return;
        }
        console.error(error);
      } else {
        setBookings(data || []);
      }
    } catch (e) {
      if (e?.message?.includes('AbortError') || e?.message?.includes('Lock broken')) {
        setTimeout(() => fetchBookings(), 1000);
        return;
      }
      console.error(e);
    }
    finally { setLoading(false); }
  };

  const handleRespond = async (bookingId, accept) => {
    setProcessing(bookingId);
    try {
      const { data, error } = await supabase?.rpc('owner_respond_to_booking', {
        p_booking_id: bookingId,
        p_owner_id: user?.id,
        p_accept: accept,
      });
      if (error) throw error;
      if (data?.success) {
        setSuccessMsg(accept ? 'Booking accepted!' : 'Booking declined');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
        fetchBookings();
      }
    } catch (e) { console.error(e); }
    finally { setProcessing(null); }
  };

  const tabs = [
    { key: 'requested', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'active', label: 'Active' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  const filtered = bookings?.filter(b => b?.booking_status === activeTab);
  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  const statusColors = {
    requested: '#fbbf24', approved: '#34d399', active: '#ffd700',
    completed: '#a78bfa', cancelled: '#f87171',
  };

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {processing && <SpinnerOverlay message="Processing response..." />}
      {showSuccess && <SuccessToast message={successMsg} visible={showSuccess} subMessage="" onDismiss={() => setShowSuccess(false)} />}
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/my-vehicles')} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
            <Icon name="ArrowLeft" size={20} className="text-white" />
          </button>
          <h1 className="text-white font-bold text-lg">Booking Requests</h1>
          <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(255,215,0,0.15)', color: '#ffd700' }}>
            {bookings?.filter(b => b?.booking_status === 'requested')?.length} pending
          </span>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs?.map(tab => (
            <button key={tab?.key} onClick={() => setActiveTab(tab?.key)}
              className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
              style={{ background: activeTab === tab?.key ? `${statusColors?.[tab?.key]}20` : 'rgba(255,255,255,0.06)', color: activeTab === tab?.key ? statusColors?.[tab?.key] : 'rgba(255,255,255,0.5)', border: `1px solid ${activeTab === tab?.key ? `${statusColors?.[tab?.key]}40` : 'rgba(255,255,255,0.1)'}` }}>
              {tab?.label}
              {tab?.key === 'requested' && bookings?.filter(b => b?.booking_status === 'requested')?.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs" style={{ background: '#fbbf24', color: '#0a1628' }}>
                  {bookings?.filter(b => b?.booking_status === 'requested')?.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3]?.map(i => <div key={i} className="h-36 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />)}
          </div>
        ) : filtered?.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-3">📋</div>
            <h3 className="text-white font-bold text-lg mb-1">No {activeTab} bookings</h3>
            <p className="text-white/40 text-sm">Booking requests will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered?.map(booking => {
              const breakdown = booking?.price_breakdown || {};
              const customer = booking?.user_profiles;
              return (
                <div key={booking?.id} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="text-white font-bold text-sm">#{booking?.booking_number}</h3>
                      <p className="text-white/40 text-xs mt-0.5">{booking?.vehicles?.title}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold capitalize" style={{ background: `${statusColors?.[booking?.booking_status]}20`, color: statusColors?.[booking?.booking_status] }}>
                      {booking?.booking_status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-xs">
                    <div><div className="text-white/40 mb-0.5">Customer</div><div className="text-white font-semibold">{customer?.full_name || 'N/A'}</div></div>
                    <div><div className="text-white/40 mb-0.5">Dates</div><div className="text-white font-semibold">{formatDate(booking?.start_at)} &rarr; {formatDate(booking?.end_at)}</div></div>
                    <div><div className="text-white/40 mb-0.5">Mode</div><div className="text-white font-semibold capitalize">{booking?.rental_mode?.replace('_', ' ')}</div></div>
                    <div><div className="text-white/40 mb-0.5">Total</div><div className="text-[#ffd700] font-bold">KES {breakdown?.total?.toLocaleString() || '—'}</div></div>
                  </div>

                  {booking?.pickup_location && (
                    <div className="flex items-center gap-2 text-xs text-white/40 mb-4">
                      <Icon name="MapPin" size={12} />
                      <span>{booking?.pickup_location}</span>
                    </div>
                  )}

                  {booking?.booking_status === 'requested' && (
                    <div className="flex gap-3">
                      <button onClick={() => handleRespond(booking?.id, true)} disabled={!!processing} className="btn-primary flex-1 text-sm py-2.5">
                        <Icon name="Check" size={16} /> Accept
                      </button>
                      <button onClick={() => handleRespond(booking?.id, false)} disabled={!!processing} className="btn-danger flex-1 text-sm py-2.5">
                        <Icon name="X" size={16} /> Decline
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingRequestsScreen;
